import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';

import { useDeleteObligationChangeAttestation } from '@/hooks/mutations/obligation-change-attestation/useDeleteObligationChangeAttestation';
import { useInsertObligationChangeAttestation } from '@/hooks/mutations/obligation-change-attestation/useInsertObligationChangeAttestation';

interface UseObligationChangeAttestationArgs {
  obligationChangeId: string | undefined;
  /**
   * The record's attestations — every user who has marked this change read.
   * Undefined until the detail query resolves.
   *
   * Narrower than the record on purpose: the hook never has to know the query's
   * shape, and its spec never has to build one.
   */
  attestations: { UserId: string }[] | undefined;
  /** The detail query's refetch. */
  refetch: () => void;
}

/**
 * Whether the current user has attested this obligation change, and the two
 * writes that move it either way.
 *
 * Both writes end in the caller's `refetch`, and that is load-bearing rather
 * than belt-and-braces: `useGetObligationChangeById` is built by
 * `createQueryHook` with the default `no-cache` fetch policy, so neither the
 * mutation hooks' Apollo eviction nor their tRPC invalidation can make the read
 * run again. Without the refetch the buttons swap only on remount (COI-577).
 */
export const useObligationChangeAttestation = ({
  obligationChangeId,
  attestations,
  refetch,
}: UseObligationChangeAttestationArgs) => {
  const { user } = useRisksmartUser();
  const {
    insertObligationChangeAttestation,
    loading: insertAttestationLoading,
  } = useInsertObligationChangeAttestation();
  const {
    deleteObligationChangeAttestation,
    loading: deleteAttestationLoading,
  } = useDeleteObligationChangeAttestation();

  const isRead = attestations?.some((a) => a.UserId === user?.userId) ?? false;

  const markAsRead = async () => {
    if (!obligationChangeId) {
      return;
    }
    await insertObligationChangeAttestation({
      ObligationChangeId: obligationChangeId,
    });
    refetch();
  };

  const markAsUnread = async () => {
    if (!obligationChangeId) {
      return;
    }
    await deleteObligationChangeAttestation({
      ObligationChangeId: obligationChangeId,
    });
    refetch();
  };

  return {
    isRead,
    markAsRead,
    markAsUnread,
    insertAttestationLoading,
    deleteAttestationLoading,
  };
};
